import React, { useState, useEffect } from 'react';
import { generateContent, getAvailableModels } from '../services/openRouterService';
import { cleanAIReport } from '../utils/reportCleaner';
import { markdownToHTML } from '../utils/markdownConverter';
import { isWeb } from '../utils/platform';
import './YouTubeSearchIntent.css';

const RATE_LIMIT = 5;
const RATE_LIMIT_WINDOW = 60 * 60 * 1000;
const STORAGE_HL_KEY = 'yt_search_trend_hl';

const HL_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ar', label: 'Arabic' },
  { value: 'yo', label: 'Yoruba' },
  { value: 'ig', label: 'Igbo' },
  { value: 'ha', label: 'Hausa' },
];

const YouTubeSearchIntent = () => {
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('idle');
  const [report, setReport] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState({ remaining: 5, resetTime: null });
  const [suggestHl, setSuggestHl] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_HL_KEY) || 'en';
    } catch {
      return 'en';
    }
  });
  /** Shown during "analyzing" when we pause for OpenRouter 429 retry */
  const [analyzingNote, setAnalyzingNote] = useState('');

  const checkRateLimit = () => {
    const searches = JSON.parse(localStorage.getItem('yt_search_trend_searches') || '[]');
    const now = Date.now();
    const recentSearches = searches.filter((timestamp) => now - timestamp < RATE_LIMIT_WINDOW);
    const remaining = Math.max(0, RATE_LIMIT - recentSearches.length);
    const oldestSearch = recentSearches.length > 0 ? Math.min(...recentSearches) : null;
    const resetTime = oldestSearch ? new Date(oldestSearch + RATE_LIMIT_WINDOW) : null;
    setRateLimitInfo({ remaining, resetTime });
    return remaining > 0;
  };

  const recordSearch = () => {
    const searches = JSON.parse(localStorage.getItem('yt_search_trend_searches') || '[]');
    const now = Date.now();
    const recentSearches = searches.filter((timestamp) => now - timestamp < RATE_LIMIT_WINDOW);
    recentSearches.push(now);
    localStorage.setItem('yt_search_trend_searches', JSON.stringify(recentSearches));
    checkRateLimit();
  };

  useEffect(() => {
    checkRateLimit();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_HL_KEY, suggestHl);
    } catch {
      /* ignore */
    }
  }, [suggestHl]);

  if (isWeb) {
    return (
      <div className="fb-search-trend yt-search-trend">
        <div className="fb-search-trend-header">
          <h2>YT Search Trend</h2>
          <p style={{ padding: 24, textAlign: 'center', fontSize: 16 }}>
            YT Search Trend is available in the Fiddyscript desktop app.
          </p>
        </div>
      </div>
    );
  }

  const generateAIReport = async (searchTerm, scrapedData) => {
    const apiKey = localStorage.getItem('openrouter_api_key');
    if (!apiKey || !apiKey.trim()) {
      throw new Error('OpenRouter API key is required. Please set it in Settings.');
    }

    const defaultModel = localStorage.getItem('default_ai_model');
    const availableModels = await getAvailableModels(false, apiKey);
    let modelToUse = defaultModel;
    if (!modelToUse || modelToUse.includes('gemini-2.0-flash-exp')) {
      const gptModel = availableModels.find(
        (m) => m.id.includes('gpt-4') || m.id.includes('gpt-3.5')
      );
      if (gptModel) {
        modelToUse = gptModel.id;
      } else {
        const fallbackModel = availableModels.find(
          (m) => m.id.includes('gemini') || m.id.includes('gpt') || m.id.includes('claude')
        );
        modelToUse = fallbackModel?.id || 'openai/gpt-3.5-turbo';
      }
    }

    const suggestions = scrapedData.autocompleteSuggestions || [];

    const systemPrompt = `You are Fiddyscript's Senior YouTube Research Analyst. Your job is to analyze YouTube search autocomplete suggestions (what viewers type into YouTube search) and turn them into actionable creator intelligence.

Core Directives:
- Treat each autocomplete line as a signal of viewer intent: what they want to watch, learn, or compare.
- Infer angles for titles, thumbnails, hooks, and series—not generic advice.
- Ignore junk: URLs, channel handles used as spam, or gibberish. Focus on real queries.
- Actionable output: specific video angles and SEO phrases creators can use this week.

Response Structure (Markdown) with these sections:
- Executive take: One paragraph on what the cluster of suggestions says about demand for "${searchTerm}".
- Top autocomplete queries: Numbered list of the strongest 7–12 suggestions from the data (use only what was provided; do not invent queries).
- Viewer intent map: Short bullets grouping intents (e.g. educational, entertainment, comparison, local/geo, news).
- Title & hook ideas: 5–8 concrete video titles or patterns (include 1–2 that use numbers or "vs" if the data supports it).
- Thumbnail & packaging: 3–5 short tips (text on thumbnail, emotion, curiosity gap) tied to these queries.
- SEO & tags: Keyword clusters and long-tail phrases to target (no stuffing; natural language).
- Content gaps: 2–4 angles competitors might be missing based on the suggestions.
- Risks / sensitivity: If any topic could be controversial or YMYL, note one line on tone and disclaimers.`;

    const userPrompt = `Seed keyword: "${searchTerm}"

YouTube autocomplete suggestions (from YouTube/Google suggest, same family as the in-site search bar):
${JSON.stringify(suggestions)}

Rules:
- Base every insight on these suggestions; you may infer intent but do not fabricate new autocomplete strings.
- If there are fewer than 7 suggestions, work with what is available.
- Prefer YouTube-native framing (watch time, CTR, retention hooks) over blog-style advice.

Produce the Markdown report with the sections listed above.`;

    const combinedPrompt = `${systemPrompt}\n\n${userPrompt}`;

    const isOpenRouterThrottle = (msg) =>
      /429|rate\s*limit|too many requests/i.test(String(msg || ''));

    const runGenerate = () => generateContent(combinedPrompt, modelToUse, apiKey);

    const mapError = (error) => {
      const m = String(error.message || '');
      if (/api key/i.test(m) && /required/i.test(m)) {
        return new Error('OpenRouter API key is required. Please set it in Settings.');
      }
      if (isOpenRouterThrottle(m)) {
        return new Error(
          'OpenRouter rate limit (429): still throttled after a 35-second automatic retry. Wait 1–2 minutes, choose another model in Settings, or check limits at openrouter.ai. (This is not your YT Search Trend 5/hour counter.)'
        );
      }
      if (error.message.includes('401') || error.message.includes('403') || /authorization/i.test(m)) {
        return new Error('OpenRouter API key is invalid. Please check your API key in Settings.');
      }
      if (error.message.includes('No endpoints found')) {
        return new Error(
          'Model not available. Please enable "Enable free endpoints that may publish prompts" at https://openrouter.ai/settings/privacy or try a different model.'
        );
      }
      if (error.message.includes('Provider returned error')) {
        return new Error('AI model error. Please try a different AI model in Settings.');
      }
      return new Error(`AI analysis failed: ${error.message}`);
    };

    try {
      try {
        return await runGenerate();
      } catch (firstErr) {
        if (!isOpenRouterThrottle(firstErr.message)) {
          throw firstErr;
        }
        setAnalyzingNote(
          'OpenRouter rate limit (429). Waiting 35 seconds, then retrying the AI request once…'
        );
        await new Promise((r) => setTimeout(r, 35000));
        setAnalyzingNote('Retrying AI request…');
        return await runGenerate();
      }
    } catch (error) {
      console.error('AI generation error:', error);
      throw mapError(error);
    } finally {
      setAnalyzingNote('');
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setErrorMessage('Please enter a keyword to search');
      setStatus('error');
      return;
    }
    if (!checkRateLimit()) {
      setErrorMessage(`Rate limit exceeded. Maximum ${RATE_LIMIT} searches per hour. Please try again later.`);
      setStatus('error');
      return;
    }

    setStatus('scraping');
    setReport(null);
    setErrorMessage('');
    setAnalyzingNote('');

    try {
      let retries = 0;
      while ((!window.electronAPI || !window.electronAPI.youtubeSearchIntent) && retries < 10) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        retries++;
      }
      if (!window.electronAPI?.youtubeSearchIntent) {
        throw new Error(
          'YT Search Trend requires the desktop app. Restart the app if this message persists.'
        );
      }

      const result = await window.electronAPI.youtubeSearchIntent.search(
        keyword.trim(),
        suggestHl || 'en'
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch YouTube suggestions');
      }

      const rawData = result.data;
      const autocompleteSuggestions = rawData?.autocompleteSuggestions || [];
      if (!autocompleteSuggestions.length) {
        throw new Error(
          'No suggestions returned. Try a different keyword or check your network connection.'
        );
      }

      setStatus('analyzing');
      const aiResponse = await generateAIReport(keyword.trim(), rawData);
      const cleanedReport = cleanAIReport(aiResponse);
      const htmlReport = markdownToHTML(cleanedReport);
      setReport(htmlReport);
      setStatus('success');
      recordSearch();
      checkRateLimit();
    } catch (error) {
      console.error('YT Search Trend error:', error);
      setStatus('error');
      setAnalyzingNote('');
      let userMessage = error.message || 'An unexpected error occurred.';
      if (/api key/i.test(userMessage) && /required|invalid/i.test(userMessage)) {
        userMessage = 'OpenRouter API key is required or invalid. Please set it in Settings.';
      }
      setErrorMessage(userMessage);
    }
  };

  const stripHTML = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleCopyReport = async () => {
    if (!report) return;
    try {
      const plainText = report.replace(/<br>/gi, '\n').replace(/<[^>]*>/g, '');
      if (window.electronAPI?.copyToClipboard) {
        window.electronAPI.copyToClipboard(plainText);
        alert('Report copied to clipboard!');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
        alert('Report copied to clipboard!');
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.style.position = 'fixed';
        textarea.style.left = '-999999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('Report copied to clipboard!');
      }
    } catch (err) {
      console.error('Copy error:', err);
      alert('Could not copy to clipboard. Please select and copy manually.');
    }
  };

  return (
    <div className="fb-search-trend yt-search-trend">
      <div className="fb-search-trend-header">
        <h2>YT Search Trend</h2>
        <p>See what viewers type into YouTube search around your seed keyword—then turn it into titles, hooks, and SEO ideas.</p>
        <div className="feature-banner">Pulls YouTube-style autocomplete suggestions and analyzes them with AI</div>

        <div className="rate-limit-info">
          <div className="rate-limit-text">
            Remaining searches: <strong>{rateLimitInfo.remaining}/{RATE_LIMIT}</strong>
          </div>
          {rateLimitInfo.resetTime && (
            <div className="reset-time">Resets {rateLimitInfo.resetTime.toLocaleTimeString()}</div>
          )}
          <div className="reset-time" style={{ marginTop: 8 }}>
            Counts only completed reports. OpenRouter (AI) can also return 429 — separate from this counter.
          </div>
        </div>

        <div className="hl-row">
          <label htmlFor="yt-hl">Suggestion language / region hint</label>
          <select
            id="yt-hl"
            value={suggestHl}
            onChange={(e) => setSuggestHl(e.target.value)}
            disabled={status === 'scraping' || status === 'analyzing'}
          >
            {HL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.value})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="search-section">
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="e.g. 10 facts, how to edit reels, affiliate marketing Nigeria"
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          disabled={status === 'scraping' || status === 'analyzing'}
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={status === 'scraping' || status === 'analyzing' || rateLimitInfo.remaining === 0}
          className={`search-button ${status === 'scraping' || status === 'analyzing' ? 'processing' : ''}`}
        >
          {status === 'scraping' || status === 'analyzing' ? (
            <>
              <span className="button-spinner" />
              <span>Processing...</span>
            </>
          ) : (
            'Analyze'
          )}
        </button>
      </div>

      {status === 'scraping' && (
        <div className="status-message scraping">
          <div className="status-content">
            <div className="status-spinner" />
            <div className="status-text-container">
              <span>Fetching YouTube search suggestions for &quot;{keyword}&quot;...</span>
            </div>
          </div>
        </div>
      )}
      {status === 'analyzing' && (
        <div className="status-message analyzing">
          <div className="status-content">
            <div className="status-spinner" />
            <div className="status-text-container">
              <span>AI is turning suggestions into creator insights...</span>
              {analyzingNote ? <span className="status-subtext">{analyzingNote}</span> : null}
            </div>
          </div>
        </div>
      )}
      {status === 'error' && errorMessage && <div className="error-message">❌ {errorMessage}</div>}

      {status === 'success' && report && (
        <div className="results-card">
          <div className="results-header">
            <h3>YouTube Search Intelligence Report</h3>
            <button type="button" onClick={handleCopyReport} className="copy-button">
              Copy Report
            </button>
          </div>
          <div
            className="report-content"
            dangerouslySetInnerHTML={{ __html: report }}
            style={{ whiteSpace: 'pre-wrap' }}
          />
        </div>
      )}
    </div>
  );
};

export default YouTubeSearchIntent;
